import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { CreditCard, FileSignature, Eye, X, ChevronLeft, ChevronRight, Loader2, SearchX, Filter, Phone, Clock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import API_BASE_URL from '../../Config';

export default function SocioCreditos({
    creditos,
    loading,
    socioId,
    token: propToken,
    formatDate,
    formatCurrency
}) {
    const { token: authToken } = useAuth();
    const token = propToken || authToken;

    // --- Modal States ---
    const [showModal, setShowModal] = useState(false);
    const [selectedCredit, setSelectedCredit] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [modalCuotas, setModalCuotas] = useState([]);
    const [modalPage, setModalPage] = useState(0);
    const [modalSize, setModalSize] = useState(10);
    const [modalTotalPages, setModalTotalPages] = useState(0);
    const [modalFilter, setModalFilter] = useState(''); // 'PENDIENTE', 'PAGADO', ''

    // --- Main Table Pagination States ---
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [goToPage, setGoToPage] = useState('1');

    // --- Sub-Modal for Payment Details ---
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedCuotaForPayment, setSelectedCuotaForPayment] = useState(null);
    const [payments, setPayments] = useState([]);
    const [paymentsLoading, setPaymentsLoading] = useState(false);
    const [compromisos, setCompromisos] = useState([]);
    const [showCompromisoModal, setShowCompromisoModal] = useState(false);
    const [selectedCompromisoForView, setSelectedCompromisoForView] = useState(null);

    // --- Fetch Commitments ---
    React.useEffect(() => {
        const fetchCompromisos = async () => {
            if (!socioId) return;
            try {
                const res = await fetch(`${API_BASE_URL}/compromisos/socio/${socioId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setCompromisos(data.content || data || []);
                }
            } catch (err) { console.error(err); }
        };
        fetchCompromisos();
    }, [socioId, token]);

    // --- Modal Logic ---
    const handleVerDetalle = async (credito) => {
        const creditoId = credito.creditoId || credito.id;
        setSelectedCredit(credito); // Mostrar temporalmente
        setShowModal(true);

        try {
            const response = await fetch(`${API_BASE_URL}/creditos/${creditoId}/detalle`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (response.ok) {
                const data = await response.json();
                setSelectedCredit(data);
            }
        } catch (error) {
            console.error("Error fetching full credit details", error);
        }

        fetchModalCuotas(creditoId, 0, modalSize, modalFilter);
    };

    const fetchModalCuotas = async (creditoId, page, size, estado) => {
        setModalLoading(true);
        try {
            let url = `${API_BASE_URL}/creditos/${creditoId}/cuotas?page=${page}&size=${size}&sort=numeroCuota,asc`;
            if (estado) url += `&estado=${estado}`;

            const response = await fetch(url, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!response.ok) throw new Error('Error cargando cuotas');
            const data = await response.json();
            setModalCuotas(data.content || []);
            setModalTotalPages(data.totalPages);
            setModalPage(page);
        } catch (err) {
            console.error(err);
        } finally {
            setModalLoading(false);
        }
    };

    const handleVerDetallePago = async (cuota) => {
        setSelectedCuotaForPayment(cuota);
        setShowPaymentModal(true);
        setPaymentsLoading(true);
        try {
            const sid = socioId || selectedCredit?.idSocio;
            const response = await fetch(`${API_BASE_URL}/creditos/pagos/socio/${sid}?page=0&size=50&sort=fechaPago,desc`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (response.ok) {
                const data = await response.json();
                const targetCreditoId = cuota.creditoId || selectedCredit?.id;
                const filtered = (data.content || []).filter(p =>
                    Number(p.creditoId) === Number(targetCreditoId) &&
                    (Number(p.numeroCuota) === Number(cuota.numeroCuota) || Number(p.nroCuota) === Number(cuota.numeroCuota))
                );
                setPayments(filtered);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setPaymentsLoading(false);
        }
    };

    const getStatusStyle = (cuota) => {
        const status = (cuota.estadoDescripcion || cuota.estadoCuota || '').toUpperCase();
        if (status.includes('CANCELADO') || status.includes('PAGADO')) return 'bg-green-100 text-green-700 border border-green-200';
        if (status.includes('PARCIAL')) return 'bg-orange-100 text-orange-700 border border-orange-200';
        if (status.includes('VENCIDA') || status.includes('MORA')) return 'bg-red-100 text-red-700 border border-red-200';
        return 'bg-slate-100 text-slate-900 border border-slate-200';
    };

    const getStatusText = (cuota) => {
        return cuota.estadoDescripcion || cuota.estadoCuota || 'PENDIENTE';
    };

    const isOverdue = (fecha) => {
        if (!fecha) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return new Date(fecha) < today;
    };

    return (
        <div className="animate-premium-in space-y-8">
            <div className="flex items-center gap-4">
                <div className="w-1.5 h-8 bg-uni-teal rounded-full" />
                <div>
                    <h2 className="text-xl font-black text-slate-800 tracking-tighter">Historial Crediticio</h2>
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Contratos de crédito, préstamos y amortizaciones</p>
                </div>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-24 text-slate-400">
                    <Loader2 className="w-10 h-10 animate-spin text-uni-teal mb-6" />
                    <p className="text-[11px] font-black uppercase tracking-[0.3em] animate-pulse">Sincronizando contratos...</p>
                </div>
            ) : !creditos || creditos.length === 0 ? (
                <div className="enterprise-card p-20 text-center border-dashed border-2 bg-slate-50/50 border-slate-200">
                    <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-4 opacity-50" />
                    <p className="text-slate-400 font-black text-[11px] uppercase tracking-widest leading-relaxed">Este socio no registra contratos de crédito<br />vigentes o históricos.</p>
                </div>
            ) : (
                <div className="enterprise-card border-none">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-slate-50">
                                <tr>
                                    <th className="py-4 px-6 text-left rounded-tl-2xl text-[10px] font-black uppercase tracking-widest text-slate-500">Nro. Prestamo</th>
                                    <th className="py-4 px-6 text-right text-[10px] font-black uppercase tracking-widest text-slate-500">Monto del prestamo</th>
                                    <th className="py-4 px-6 text-right text-[10px] font-black uppercase tracking-widest text-slate-500">Saldo Pendiente (Gs.)</th>
                                    <th className="py-4 px-6 text-center text-[10px] font-black uppercase tracking-widest text-slate-500">Estado</th>
                                    <th className="py-4 px-6 text-right rounded-tr-2xl text-[10px] font-black uppercase tracking-widest text-slate-500"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y-2 divide-slate-100">
                                {(() => {
                                    const indexOfLastItem = currentPage * itemsPerPage;
                                    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
                                    const currentCreditos = creditos?.slice(indexOfFirstItem, indexOfLastItem) || [];

                                    return currentCreditos.map((credito) => {
                                        const saldoPendiente = Number(credito.saldoCapitalPendiente) || 0;
                                        const monto = Number(credito.monto) || 0;
                                        const compromisoActivo = compromisos.find(cp => ((cp.credito?.id || cp.creditoId || cp.credito_id) == credito.id) && cp.estadoCompromiso === 1);

                                        return (
                                            <tr key={credito.id} className="hover:bg-blue-50/40 transition-colors group">
                                                <td className="py-5 px-6">
                                                    <div className="flex flex-col gap-1">
                                                        <div className="font-black text-slate-900 tracking-tight group-hover:text-uni-teal transition-colors flex items-center gap-2">
                                                            {credito.nroOperacion || `#${credito.id}`}
                                                            {compromisoActivo && (
                                                                <div className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-md text-[8px] font-black uppercase animate-pulse border border-amber-200">
                                                                    <Phone className="w-2.5 h-2.5" />
                                                                    Compromiso Pendiente
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="py-5 px-6 text-right">
                                                    <div className="font-black text-slate-800 text-sm">
                                                        {formatCurrency(monto)}
                                                    </div>
                                                </td>
                                                <td className="py-5 px-6 text-right">
                                                    <div className="font-black text-slate-800 text-sm">
                                                        {formatCurrency(saldoPendiente)}
                                                    </div>
                                                </td>
                                                <td className="py-5 px-6 text-center">
                                                    <span className={`text-[9px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full border shadow-sm
                                                    ${credito.estadoNombre === 'Vigente' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                                            credito.estadoNombre?.toUpperCase().includes('CANCELADO') ? 'bg-slate-100 text-slate-600 border-slate-300' :
                                                                'bg-slate-50 text-slate-500 border-slate-200'}
                                                `}>
                                                        {credito.estadoNombre || 'PENDIENTE'}
                                                    </span>
                                                </td>
                                                <td className="py-5 px-6 text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <button
                                                            onClick={() => handleVerDetalle(credito)}
                                                            className="inline-flex items-center gap-2 px-3 py-2 bg-slate-900 text-white rounded-lg hover:bg-uni-teal transition-all shadow-md shadow-slate-900/10 text-[10px] font-black uppercase tracking-widest"
                                                            title="Ver Crédito"
                                                        >
                                                            <Eye className="w-4 h-4" />
                                                            <span>Ver Crédito</span>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    });
                                })()}
                            </tbody>
                        </table>
                    </div>

                    {/* PAGINATION FOOTER */}
                    {creditos && creditos.length > 0 && (
                        <div className="bg-white px-6 py-4 border-t border-slate-100 flex items-center justify-between rounded-b-2xl">
                            <div className="flex items-center gap-4 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                                <div className="flex items-center gap-2">
                                    <span>Ir a</span>
                                    <input
                                        type="number"
                                        min="1"
                                        max={Math.ceil((creditos?.length || 0) / itemsPerPage) || 1}
                                        value={goToPage}
                                        onChange={(e) => setGoToPage(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                const maxPage = Math.ceil((creditos?.length || 0) / itemsPerPage) || 1;
                                                const page = Math.max(1, Math.min(maxPage, Number(goToPage)));
                                                setCurrentPage(page);
                                                setGoToPage(page.toString());
                                            }
                                        }}
                                        onBlur={() => {
                                            const maxPage = Math.ceil((creditos?.length || 0) / itemsPerPage) || 1;
                                            const page = Math.max(1, Math.min(maxPage, Number(goToPage)));
                                            setCurrentPage(page);
                                            setGoToPage(page.toString());
                                        }}
                                        className="w-14 h-9 text-center border border-slate-200 rounded-xl focus:outline-none focus:border-uni-teal focus:ring-1 focus:ring-uni-teal/20 transition-all font-black text-slate-700 bg-slate-50"
                                    />
                                </div>
                                <div className="flex items-center gap-2">
                                    <span>Ver</span>
                                    <select
                                        value={itemsPerPage}
                                        onChange={(e) => {
                                            setItemsPerPage(Number(e.target.value));
                                            setCurrentPage(1);
                                            setGoToPage('1');
                                        }}
                                        className="h-9 pl-3 pr-8 border border-slate-200 rounded-xl focus:outline-none focus:border-uni-teal focus:ring-1 focus:ring-uni-teal/20 transition-all font-black text-slate-700 bg-slate-50 appearance-none cursor-pointer bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%2210%22%20height%3D%226%22%20viewBox%3D%220%200%2010%206%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M1%201L5%205L9%201%22%20stroke%3D%22%2364748B%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E')] bg-[length:10px_6px] bg-[right_10px_center] bg-no-repeat"
                                    >
                                        <option value={5}>5</option>
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                        <option value={50}>50</option>
                                    </select>
                                </div>
                            </div>

                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => { setCurrentPage(1); setGoToPage('1'); }}
                                    disabled={currentPage === 1}
                                    className="w-9 h-9 flex items-center justify-center rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                    <ChevronLeft className="w-4 h-4 -ml-2" />
                                </button>
                                <button
                                    onClick={() => { setCurrentPage(prev => Math.max(1, prev - 1)); setGoToPage(Math.max(1, currentPage - 1).toString()); }}
                                    disabled={currentPage === 1}
                                    className="w-9 h-9 flex items-center justify-center rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>

                                <div className="flex items-center gap-1 mx-2">
                                    {(() => {
                                        const totalPages = Math.ceil((creditos?.length || 0) / itemsPerPage) || 1;
                                        return [...Array(totalPages)].map((_, i) => {
                                            const page = i + 1;
                                            if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                                                return (
                                                    <button
                                                        key={page}
                                                        onClick={() => { setCurrentPage(page); setGoToPage(page.toString()); }}
                                                        className={`w-9 h-9 flex items-center justify-center rounded-xl font-black text-[11px] transition-all ${currentPage === page
                                                            ? 'bg-[#2B59C3] text-white shadow-md shadow-blue-900/20'
                                                            : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'
                                                            }`}
                                                    >
                                                        {page}
                                                    </button>
                                                );
                                            } else if (page === currentPage - 2 || page === currentPage + 2) {
                                                return <span key={page} className="text-slate-300 font-bold tracking-widest px-1">...</span>;
                                            }
                                            return null;
                                        });
                                    })()}
                                </div>

                                <button
                                    onClick={() => {
                                        const totalPages = Math.ceil((creditos?.length || 0) / itemsPerPage) || 1;
                                        setCurrentPage(prev => Math.min(totalPages, prev + 1));
                                        setGoToPage(Math.min(totalPages, currentPage + 1).toString());
                                    }}
                                    disabled={currentPage === (Math.ceil((creditos?.length || 0) / itemsPerPage) || 1)}
                                    className="w-9 h-9 flex items-center justify-center rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => {
                                        const totalPages = Math.ceil((creditos?.length || 0) / itemsPerPage) || 1;
                                        setCurrentPage(totalPages);
                                        setGoToPage(totalPages.toString());
                                    }}
                                    disabled={currentPage === (Math.ceil((creditos?.length || 0) / itemsPerPage) || 1)}
                                    className="w-9 h-9 flex items-center justify-center rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                    <ChevronRight className="w-4 h-4 -ml-2" />
                                </button>
                            </div>

                            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                                {(() => {
                                    const totalItems = creditos?.length || 0;
                                    const start = totalItems === 0 ? 0 : ((currentPage - 1) * itemsPerPage) + 1;
                                    const end = Math.min(currentPage * itemsPerPage, totalItems);
                                    return `[${start} A ${end} DE ${totalItems}]`;
                                })()}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* MODAL DETALLES */}
            {showModal && selectedCredit && createPortal(
                <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-[9999] flex items-start sm:items-center justify-center p-4 pt-20 sm:p-8 animate-premium-in overflow-y-auto">
                    <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-6xl max-h-[85vh] flex flex-col overflow-hidden my-auto shrink-0">
                        {/* Modal Header */}
                        <div className="bg-slate-900 text-white px-10 py-8 flex justify-between items-center shrink-0">
                            <div className="flex items-center gap-6">
                                <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center border border-white/10">
                                    <CreditCard className="w-7 h-7 text-uni-teal" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-black tracking-tight flex items-center gap-3">
                                        Detalle de Amortización {selectedCredit.nroOperacion || `#${selectedCredit.id}`}
                                    </h2>
                                    <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mt-1">
                                        Fecha Ref: {formatDate(selectedCredit.fechaDesembolso || selectedCredit.fechaSolicitud || selectedCredit.fecha)} • Capital: {formatCurrency(selectedCredit.monto)}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowModal(false)}
                                className="p-3 hover:bg-rose-500 rounded-2xl transition-all group"
                            >
                                <X className="w-6 h-6 group-hover:scale-110 transition-transform" />
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div className="flex-grow overflow-auto p-10 bg-white">
                            {/* --- SCRIPTCASE GRID DETAIL STYLE --- */}
                            <div className="mb-12 space-y-6">
                                {/* Sección 1: Datos de la Solicitud / Crédito */}
                                <div>
                                    <div className="flex items-center gap-2 mb-4 px-1">
                                        <div className="w-0 h-0 border-t-[4px] border-t-transparent border-l-[6px] border-l-slate-500 border-b-[4px] border-b-transparent"></div>
                                        <h3 className="text-sm font-bold text-slate-800 tracking-tight">Datos de la Solicitud / Operación</h3>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 px-5">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">Nro. Solicitud</span>
                                            <span className="text-xs text-slate-600">{selectedCredit.nro_solicitud || selectedCredit.nroSolicitud || '-'}</span>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">Nro. Operación</span>
                                            <span className="text-xs text-slate-600">{selectedCredit.nro_operacion || selectedCredit.nroOperacion || '-'}</span>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">ID Préstamo (Sist)</span>
                                            <span className="text-xs text-slate-600">{selectedCredit.id_prestamo || selectedCredit.id}</span>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">Estado</span>
                                            <span className="text-xs text-slate-600">{selectedCredit.estadoNombre || selectedCredit.estado_credito_id || 'Pendiente'}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="h-px w-full bg-slate-100 my-4" />

                                {/* Sección 2: Detalles Financieros */}
                                <div>
                                    <div className="flex items-center gap-2 mb-4 px-1">
                                        <div className="w-0 h-0 border-t-[4px] border-t-transparent border-l-[6px] border-l-slate-500 border-b-[4px] border-b-transparent"></div>
                                        <h3 className="text-sm font-bold text-slate-800 tracking-tight">Detalles Financieros</h3>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-5 gap-8 px-5">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">Monto Aprobado</span>
                                            <span className="text-xs text-slate-600">{formatCurrency(selectedCredit.monto)}</span>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">T.A.N. (%)</span>
                                            <span className="text-xs text-slate-600">{selectedCredit.tan ? `${selectedCredit.tan}%` : '-'}</span>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">Total Capital Pagado</span>
                                            <span className="text-xs text-emerald-600 font-semibold">{formatCurrency(selectedCredit.total_pagado_capital || selectedCredit.totalPagadoCapital || 0)}</span>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">Saldo Capital Pend.</span>
                                            <span className="text-xs text-rose-500 font-semibold">{formatCurrency(selectedCredit.saldo_capital_pendiente || selectedCredit.saldoCapitalPendiente || 0)}</span>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">Saldo Interés Pend.</span>
                                            <span className="text-xs text-rose-500 font-semibold">{formatCurrency(selectedCredit.saldo_interes_pendiente || selectedCredit.saldoInteresPendiente || 0)}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="h-px w-full bg-slate-100 my-4" />

                                {/* Sección 3: Fechas y Plazos */}
                                <div>
                                    <div className="flex items-center gap-2 mb-4 px-1">
                                        <div className="w-0 h-0 border-t-[4px] border-t-transparent border-l-[6px] border-l-slate-500 border-b-[4px] border-b-transparent"></div>
                                        <h3 className="text-sm font-bold text-slate-800 tracking-tight">Fechas y Plazos</h3>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-6 gap-6 px-5">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">Desembolso</span>
                                            <span className="text-xs text-slate-600">{selectedCredit.fecha_desembolso || selectedCredit.fechaDesembolso ? formatDate(selectedCredit.fecha_desembolso || selectedCredit.fechaDesembolso) : '-'}</span>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">1er Vto.</span>
                                            <span className="text-xs text-slate-600">{selectedCredit.fecha_primer_vencimiento || selectedCredit.fechaPrimerVencimiento ? formatDate(selectedCredit.fecha_primer_vencimiento || selectedCredit.fechaPrimerVencimiento) : '-'}</span>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">Día de Pago</span>
                                            <span className="text-xs text-slate-600">{selectedCredit.dia_pago || selectedCredit.diaPago ? `Día ${selectedCredit.dia_pago || selectedCredit.diaPago}` : '-'}</span>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">Plazo</span>
                                            <span className="text-xs text-slate-600">{selectedCredit.plazo_meses || selectedCredit.plazoMeses || selectedCredit.plazo || '-'} Meses</span>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">Último Pago</span>
                                            <span className="text-xs text-slate-600">{selectedCredit.fecha_ultimo_pago || selectedCredit.fechaUltimoPago ? formatDate(selectedCredit.fecha_ultimo_pago || selectedCredit.fechaUltimoPago) : '-'}</span>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">Cancelación</span>
                                            <span className="text-xs text-slate-600">{selectedCredit.fecha_cancelacion || selectedCredit.fechaCancelacion ? formatDate(selectedCredit.fecha_cancelacion || selectedCredit.fechaCancelacion) : '-'}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="h-px w-full bg-slate-100 my-4" />

                                {/* Sección 4: Configuración del Crédito */}
                                <div>
                                    <div className="flex items-center gap-2 mb-4 px-1">
                                        <div className="w-0 h-0 border-t-[4px] border-t-transparent border-l-[6px] border-l-slate-500 border-b-[4px] border-b-transparent"></div>
                                        <h3 className="text-sm font-bold text-slate-800 tracking-tight">Configuración del Crédito</h3>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 px-5">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">Contrato Principal ID</span>
                                            <span className="text-xs text-slate-600">{selectedCredit.contrato_principal_id ?? selectedCredit.contratoPrincipalId ?? '-'}</span>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">Destino</span>
                                            <span className="text-xs text-slate-600">{selectedCredit.destino ?? '-'}</span>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">Estado</span>
                                            <span className="text-xs text-slate-600">{selectedCredit.estadoNombre ?? '-'}</span>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">Línea Crédito</span>
                                            <span className="text-xs text-slate-600">{selectedCredit.lineaCredito ?? '-'}</span>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">Método Amortización</span>
                                            <span className="text-xs text-slate-600">{selectedCredit.metodoAmortizacion ?? '-'}</span>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">Moneda</span>
                                            <span className="text-xs text-slate-600">{selectedCredit.moneda ?? '-'}</span>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">Tasa Aplicada</span>
                                            <span className="text-xs text-slate-600">{selectedCredit.tasaNombre ?? '-'}</span>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">Tipo Crédito</span>
                                            <span className="text-xs text-slate-600">{selectedCredit.tipoCredito ?? '-'}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="h-px w-full bg-slate-100 my-4" />

                                {/* Sección 5: Auditoría y Trazabilidad */}
                                <div>
                                    <div className="flex items-center gap-2 mb-4 px-1">
                                        <div className="w-0 h-0 border-t-[4px] border-t-transparent border-l-[6px] border-l-slate-500 border-b-[4px] border-b-transparent"></div>
                                        <h3 className="text-sm font-bold text-slate-800 tracking-tight">Auditoría y Trazabilidad</h3>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 px-5">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">Canal Desembolso</span>
                                            <span className="text-xs text-slate-600">{selectedCredit.canal_desembolso ?? selectedCredit.canalDesembolso ?? '-'}</span>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">Cuenta Desembolso ID</span>
                                            <span className="text-xs text-slate-600">{selectedCredit.cuenta_desembolso_id ?? selectedCredit.cuentaDesembolsoId ?? '-'}</span>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">Sucursal</span>
                                            <span className="text-xs text-slate-600">{selectedCredit.sucursal ?? '-'}</span>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">Usuario Desembolso</span>
                                            <span className="text-xs text-slate-600">{selectedCredit.usuarioDesembolso ?? '-'}</span>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">Usuario Registro</span>
                                            <span className="text-xs text-slate-600">{selectedCredit.usuarioRegistro ?? '-'}</span>
                                        </div>
                                        <div className="flex flex-col gap-1 col-span-2">
                                            <span className="text-xs font-bold text-slate-800">Timestamp Registro</span>
                                            <span className="text-xs text-slate-600">{selectedCredit.timestamp_registro ?? selectedCredit.timestampRegistro ?? '-'}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="h-px w-full bg-slate-100 my-4" />

                                {/* Sección 6: Información Adicional */}
                                <div>
                                    <div className="flex items-center gap-2 mb-4 px-1">
                                        <div className="w-0 h-0 border-t-[4px] border-t-transparent border-l-[6px] border-l-slate-500 border-b-[4px] border-b-transparent"></div>
                                        <h3 className="text-sm font-bold text-slate-800 tracking-tight">Información Adicional</h3>
                                    </div>
                                    <div className="grid grid-cols-1 px-5">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-800">Observaciones</span>
                                            <span className="text-xs text-slate-600 bg-slate-50 p-3 rounded-lg border border-slate-100 mt-1 min-h-[40px]">
                                                {selectedCredit.observaciones || 'Sin observaciones registradas.'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="h-px w-full bg-slate-100 my-4" />

                                {/* Header Amortización */}
                                <div className="flex items-center gap-2 mb-2 px-1">
                                    <div className="w-0 h-0 border-t-[6px] border-t-slate-500 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent"></div>
                                    <h3 className="text-sm font-bold text-slate-800 tracking-tight">Detalle de Amortización</h3>
                                </div>
                            </div>

                            {/* Modal Filters */}
                            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-6 px-5">
                                <div className="flex bg-slate-50 p-1.5 rounded-2xl gap-2 border border-slate-200 shadow-sm">
                                    <button onClick={() => { setModalFilter(''); fetchModalCuotas(selectedCredit.id, 0, modalSize, ''); }} className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${modalFilter === '' ? 'bg-slate-900 text-white shadow-xl shadow-slate-900/10' : 'text-slate-400 hover:text-slate-900'}`}>Todas</button>
                                    <button onClick={() => { setModalFilter('PENDIENTE'); fetchModalCuotas(selectedCredit.id, 0, modalSize, 'PENDIENTE'); }} className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${modalFilter === 'PENDIENTE' ? 'bg-amber-500 text-white shadow-xl shadow-amber-500/10' : 'text-slate-400 hover:text-slate-900'}`}>Pendientes</button>
                                    <button onClick={() => { setModalFilter('PAGADO'); fetchModalCuotas(selectedCredit.id, 0, modalSize, 'PAGADO'); }} className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${modalFilter === 'PAGADO' ? 'bg-emerald-600 text-white shadow-xl shadow-emerald-600/10' : 'text-slate-400 hover:text-slate-900'}`}>Pagadas</button>
                                </div>

                                <div className="flex items-center gap-4 bg-white px-5 py-3 border border-slate-200 rounded-2xl shadow-sm">
                                    <Filter className="w-4 h-4 text-slate-300" />
                                    <select
                                        value={modalSize}
                                        onChange={(e) => {
                                            const newSize = parseInt(e.target.value);
                                            setModalSize(newSize);
                                            fetchModalCuotas(selectedCredit.id, 0, newSize, modalFilter);
                                        }}
                                        className="bg-transparent text-[11px] font-black text-slate-600 outline-none cursor-pointer uppercase tracking-widest"
                                    >
                                        <option value="10">10 por página</option>
                                        <option value="20">20 por página</option>
                                        <option value="50">50 por página</option>
                                    </select>
                                </div>
                            </div>

                            {/* Modal Table */}
                            <div className="enterprise-card border-none bg-white">
                                {modalLoading ? (
                                    <div className="py-32 flex flex-col items-center justify-center text-slate-400">
                                        <Loader2 className="w-12 h-12 animate-spin text-uni-teal mb-6" />
                                        <p className="text-[11px] font-black uppercase tracking-[0.3em]">Consultando amortizaciones...</p>
                                    </div>
                                ) : modalCuotas.length === 0 ? (
                                    <div className="py-32 text-center text-slate-400 flex flex-col items-center">
                                        <SearchX className="w-16 h-16 mb-6 opacity-10" />
                                        <p className="text-[11px] font-black uppercase tracking-[0.2em]">No se encontraron cuotas</p>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left border-collapse">
                                            <thead>
                                                <tr className="bg-slate-100/50">
                                                    <th className="py-2 px-3 text-[10px] font-black uppercase tracking-widest text-slate-500 rounded-tl-lg">N° Cuota</th>
                                                    <th className="py-2 px-3 text-[10px] font-black uppercase tracking-widest text-slate-500 text-center">Vencimiento</th>
                                                    <th className="py-2 px-3 text-[10px] font-black uppercase tracking-widest text-slate-500 text-right">Total Cuota</th>
                                                    <th className="py-2 px-3 text-[10px] font-black uppercase tracking-widest text-slate-500 text-right">Saldo</th>
                                                    <th className="py-2 px-3 text-[10px] font-black uppercase tracking-widest text-slate-500 text-center">Estado</th>
                                                    <th className="py-2 px-3 text-[10px] font-black uppercase tracking-widest text-slate-500 text-right rounded-tr-lg">Acciones</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {modalCuotas.map((c) => {
                                                    const saldo = c.saldoCuota ?? c.saldoCapitalPendiente ?? 0;
                                                    const overdue = isOverdue(c.fechaVencimiento) && saldo > 0;
                                                    return (
                                                        <tr key={c.id} className={`hover:bg-slate-50 transition-colors ${overdue ? 'bg-rose-50/10' : ''}`}>
                                                            <td className="py-1.5 px-3">
                                                                <span className="font-bold text-slate-500 font-mono text-xs">#{String(c.numeroCuota).padStart(2, '0')}</span>
                                                            </td>
                                                            <td className={`py-1.5 px-3 text-center text-xs font-semibold ${overdue ? 'text-rose-600' : 'text-slate-600'}`}>
                                                                {formatDate(c.fechaVencimiento)}
                                                            </td>
                                                            <td className="py-1.5 px-3 text-right text-xs font-bold text-slate-800">
                                                                {formatCurrency(c.montoTotalCuota)}
                                                            </td>
                                                            <td className="py-1.5 px-3 text-right text-xs font-bold">
                                                                <span className={saldo > 0 ? (overdue ? 'text-rose-600' : 'text-amber-600') : 'text-slate-300'}>
                                                                    {formatCurrency(saldo)}
                                                                </span>
                                                            </td>
                                                            <td className="py-1.5 px-3 text-center">
                                                                <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded border ${getStatusStyle(c)}`}>
                                                                    {getStatusText(c)}
                                                                </span>
                                                            </td>
                                                            <td className="py-1.5 px-3 text-right">
                                                                <div className="flex justify-end gap-2 items-center">
                                                                    {(getStatusText(c).toUpperCase().includes('CANCELADO') || getStatusText(c).toUpperCase().includes('PARCIAL')) && (
                                                                        <button
                                                                            onClick={() => handleVerDetallePago(c)}
                                                                            className="flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded hover:bg-emerald-600 hover:text-white transition-all text-[10px] font-bold border border-emerald-100 shadow-sm"
                                                                            title="Historial de Pagos"
                                                                        >
                                                                            <Clock className="w-3 h-3" /> Pagos
                                                                        </button>
                                                                    )}
                                                                    {Array.isArray(compromisos) && compromisos.find(cp =>
                                                                        ((cp.credito?.id || cp.credito || cp.creditoId || cp.credito_id) == (c.creditoId || c.credito_id || selectedCredit?.id)) &&
                                                                        (cp.estadoCompromiso == 1 || cp.estado_compromiso == 1)
                                                                    ) && (
                                                                            <button
                                                                                onClick={() => {
                                                                                    setSelectedCompromisoForView(compromisos.find(cp =>
                                                                                        ((cp.credito?.id || cp.credito || cp.creditoId || cp.credito_id) == (c.creditoId || c.credito_id || selectedCredit?.id)) &&
                                                                                        (cp.estadoCompromiso == 1 || cp.estado_compromiso == 1)
                                                                                    ));
                                                                                    setShowCompromisoModal(true);
                                                                                }}
                                                                                className="flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-600 rounded hover:bg-amber-500 hover:text-white transition-all text-[10px] font-bold border border-amber-100 shadow-sm"
                                                                                title="Ver Compromiso"
                                                                            >
                                                                                <Phone className="w-3 h-3" /> Compromiso
                                                                            </button>
                                                                        )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>

                            {/* Modal Pagination */}
                            {!modalLoading && modalTotalPages > 1 && (
                                <div className="flex justify-between items-center mt-10">
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Página {modalPage + 1} de {modalTotalPages}</span>
                                    <div className="flex gap-4">
                                        <button
                                            disabled={modalPage === 0}
                                            onClick={() => fetchModalCuotas(selectedCredit.id, modalPage - 1, modalSize, modalFilter)}
                                            className="w-12 h-12 flex items-center justify-center bg-white border border-slate-200 rounded-2xl text-slate-600 hover:bg-slate-900 hover:text-white disabled:opacity-20 transition-all shadow-sm active:scale-95"
                                        >
                                            <ChevronLeft className="w-6 h-6" />
                                        </button>
                                        <button
                                            disabled={modalPage >= modalTotalPages - 1}
                                            onClick={() => fetchModalCuotas(selectedCredit.id, modalPage + 1, modalSize, modalFilter)}
                                            className="w-12 h-12 flex items-center justify-center bg-white border border-slate-200 rounded-2xl text-slate-600 hover:bg-slate-900 hover:text-white disabled:opacity-20 transition-all shadow-sm active:scale-95"
                                        >
                                            <ChevronRight className="w-6 h-6" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="px-10 py-6 border-t border-slate-100 flex justify-end bg-slate-50/50 shrink-0">
                            <button
                                onClick={() => setShowModal(false)}
                                className="px-12 py-4 bg-slate-900 text-white rounded-full text-[11px] font-black uppercase tracking-widest hover:bg-uni-teal transition-all shadow-xl shadow-slate-900/10"
                            >
                                Cerrar Consulta
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* SUB-MODAL DETALLE DE PAGO */}
            {showPaymentModal && selectedCuotaForPayment && createPortal(
                <div className="fixed inset-0 bg-slate-900/40 z-[99999] flex items-start sm:items-center justify-center p-4 pt-20 sm:p-8 animate-in fade-in overflow-y-auto">
                    <div className="bg-white rounded-enterprise shadow-premium w-full max-w-4xl overflow-hidden animate-in zoom-in-95 my-auto shrink-0">
                        <div className="bg-emerald-600 text-white px-6 py-4 flex justify-between items-center">
                            <h3 className="text-sm font-bold uppercase tracking-wider">Historial de Cobros - Cuota #{selectedCuotaForPayment.numeroCuota}</h3>
                            <button onClick={() => setShowPaymentModal(false)} className="hover:bg-white/10 p-1.5 rounded-enterprise transition">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-0">
                            {paymentsLoading ? (
                                <div className="py-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>
                            ) : payments.length === 0 ? (
                                <div className="py-16 text-center text-slate-400 font-bold uppercase text-[11px]">No se hallaron registros de pago asociados.</div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-[11px] border-collapse">
                                        <thead className="bg-slate-50 border-b border-enterprise-border text-slate-500 font-black uppercase tracking-tighter">
                                            <tr>
                                                <th className="px-4 py-3 text-left">Ref.</th>
                                                <th className="px-4 py-3 text-left">Fecha / Hora</th>
                                                <th className="px-4 py-3 text-left">Sucursal</th>
                                                <th className="px-4 py-3 text-left">Forma de Pago</th>
                                                <th className="px-4 py-3 text-right">Capital</th>
                                                <th className="px-4 py-3 text-right">Interés</th>
                                                <th className="px-4 py-3 text-right text-rose-600">Total</th>
                                                <th className="px-4 py-3 text-center">Transacción</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {payments.map((p, i) => (
                                                <tr key={i} className="hover:bg-emerald-50/30 transition-colors">
                                                    <td className="px-4 py-3 font-bold text-slate-400 font-mono italic">#{p.id}</td>
                                                    <td className="px-4 py-3 font-medium text-slate-700 whitespace-nowrap">
                                                        {formatDate(p.fechaPago)} <span className="text-[9px] opacity-40 ml-1">{new Date(p.fechaPago).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                    </td>
                                                    <td className="px-4 py-3 text-left font-bold text-slate-600">
                                                        {p.sucursal || 'Sede Central'}
                                                    </td>
                                                    <td className="px-4 py-3 text-left">
                                                        <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-[9px] font-black uppercase tracking-widest border border-emerald-200">
                                                            {p.metodoPago || p.formaPago || 'EFECTIVO'}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-right font-bold text-slate-900">{formatCurrency(p.montoCapital || 0)}</td>
                                                    <td className="px-4 py-3 text-right font-bold text-slate-600">{formatCurrency(p.montoInteres || 0)}</td>
                                                    <td className="px-4 py-3 text-right font-black text-emerald-700 bg-emerald-50/20">{formatCurrency(p.montoPagado)}</td>
                                                    <td className="px-4 py-3 text-center">
                                                        <span className="inline-flex items-center px-3 py-1 bg-slate-100 text-slate-900 rounded-lg font-mono text-[10px] font-black border border-slate-300 shadow-sm">
                                                            {p.transactionId || '---'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                        <div className="px-6 py-4 bg-slate-50 text-right border-t border-slate-100">
                            <button onClick={() => setShowPaymentModal(false)} className="px-6 py-2 bg-white border border-enterprise-border text-slate-700 rounded-enterprise text-xs font-bold hover:bg-slate-100 transition shadow-sm">
                                Cerrar Detalles
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* SUB-MODAL DETALLE DE COMPROMISO */}
            {showCompromisoModal && selectedCompromisoForView && createPortal(
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[99999] flex items-center justify-center p-4 animate-premium-in">
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-lg overflow-hidden border border-slate-200">
                        <div className="bg-amber-500 text-white px-8 py-6 flex justify-between items-center">
                            <div className="flex items-center gap-4">
                                <div className="p-2 bg-white/20 rounded-lg">
                                    <Phone className="w-5 h-5" />
                                </div>
                                <h3 className="font-black uppercase tracking-widest text-sm">Detalle de Compromiso</h3>
                            </div>
                            <button onClick={() => setShowCompromisoModal(false)} className="hover:rotate-90 transition-transform">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-8 space-y-6">
                            <div className="grid grid-cols-2 gap-6">
                                <div className="space-y-1">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Fecha Promesa</p>
                                    <p className="text-sm font-black text-slate-800">{formatDate(selectedCompromisoForView.fechaPromesa)}</p>
                                </div>
                                <div className="space-y-1 text-right">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Monto Comprometido</p>
                                    <p className="text-sm font-black text-amber-600">{formatCurrency(selectedCompromisoForView.montoPrometido)}</p>
                                </div>
                            </div>

                            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Observación del Operador</p>
                                <p className="text-xs font-medium text-slate-600 italic leading-relaxed">
                                    "{selectedCompromisoForView.observacion || 'Sin observaciones registradas.'}"
                                </p>
                            </div>

                            <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Estado: PENDIENTE</span>
                                </div>
                                <p className="text-[9px] font-bold text-slate-300 uppercase">Ref: #CP-{selectedCompromisoForView.id}</p>
                            </div>
                        </div>

                        <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 flex justify-end">
                            <button
                                onClick={() => setShowCompromisoModal(false)}
                                className="px-8 py-2.5 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/20"
                            >
                                Entendido
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}
